import { useEffect, useRef, useState } from 'react';
import { View, Pressable, Text, StyleSheet, Animated, Alert, Platform } from 'react-native';
import * as WebBrowser from "expo-web-browser";
import { makeRedirectUri, useAuthRequest, useAutoDiscovery, exchangeCodeAsync, ResponseType } from 'expo-auth-session';
import { MicrosoftIcon } from '@assets/--native';
import React from 'react';
import styles from '@stylesheet';
import { createURL } from 'expo-linking';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';
import { getAuthSessionGuard } from '@utils/authSessionGuard';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { useDispatch } from 'react-redux';

WebBrowser.maybeCompleteAuthSession();

// Get auth session guard singleton
const authGuard = getAuthSessionGuard();

const MicrosoftButton = ({ code }) => {
  //Microsoft Authentication Vars
  const dispatch = useDispatch();
  const [token, setToken] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const discovery = useAutoDiscovery('https://login.microsoftonline.com/common/v2.0');

  const [request, response, promptAsync] = useAuthRequest(
    {
      clientId: 'd9317384-abec-4b01-bcdd-db0da0803901',
      scopes: ['openid', 'profile', 'email', 'offline_access'],
      redirectUri: createURL(''),
      responseType: ResponseType.Code,
      usePKCE: true,
    },
    discovery
  );

    useEffect(() => {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }).start();
    }, []);

  useEffect(() => {
    const handleAuthResponse = async () => {
      if (response?.type === "success") {
        // If authentication object exists, use it directly
        if (response?.authentication?.accessToken) {
          setToken(response.authentication.accessToken);
        } 
        // Otherwise, exchange the authorization code for an access token
        else if (response?.params?.code && discovery && request) {
          try {
            // Exchange code for token (PKCE code verifier is handled automatically by expo-auth-session)
            const tokenResult = await exchangeCodeAsync(
              {
                clientId: 'd9317384-abec-4b01-bcdd-db0da0803901',
                code: response.params.code,
                redirectUri: createURL(''),
                extraParams: {},
              },
              discovery
            );

            if (tokenResult?.accessToken) {
              setToken(tokenResult.accessToken);
              console.log('Microsoft token exchange successful');
            } else {
              console.warn('Token exchange completed but no access token received');
            }
          } catch (error) {
            console.error('Failed to exchange code for token:', error);
            dispatch({
              type: SHOW_AUTH_MODAL,
              data: {
                content: `Failed to complete authentication: ${error.message || 'Please try again.'}`,
                type: 'error'
              }
            });
          }
        } else if (response?.params?.code) {
          console.warn('Cannot exchange code: missing discovery or request object');
        }
      } else if (response?.type === "error") {
        console.error('Microsoft auth error:', response.error);
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: response.error?.message || 'Authentication failed. Please try again.',
            type: 'error'
          }
        });
      }
    };

    handleAuthResponse();
  }, [response, discovery, request, dispatch]);


  const handleClick = async () => {
    // Check if another auth session is active
    if (authGuard.isSessionActive()) {
      const activeSession = authGuard.getActiveSession();
      console.warn(`Cannot start Microsoft auth - ${activeSession} is already active`);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Please wait for ${activeSession} authentication to complete`,
          type: 'error'
        }
      });
      return;
    }

    // Start auth session with guard
    const res = await authGuard.startSession('Microsoft', async () => {
      return await promptAsync();
    });

    console.log('Microsoft auth result:', res);
    return res;
  };

  return (
    <Pressable onPress={handleClick} style={{ minHeight: Platform.select({ web: 50, default: 50 }), height: 50, maxHeight: 50, flex: 1, elevation: 1, shadowRadius: 10, shadowOpacity: .2, backgroundColor: 'white', borderRadius: 30, flexDirection: 'row', overflow: "hidden", justifyContent: 'center', alignContent: 'center', alignItems: 'stretch', }}>
      {(state) => {
        const hovered = 'hovered' in state ? state.hovered : false;
        return (
        <View style={{ flex: 1 }}>

          {code && (
            <Animated.View
              style={{
                ...StyleSheet.absoluteFillObject,
                opacity: fadeAnim,
                borderRadius: 10,
              }}
            >
              <LinearGradient
                colors={['#E4D337', '#FF5733']}
                style={{ flex: 1, borderRadius: 10 }}
              />
            </Animated.View>
          )}
        <Animatable.View useNativeDriver style={{ flex: 1, paddingHorizontal: "10%", flexDirection: "row", backgroundColor: hovered ? "rgba(90,192,234,0.5)" : "rgba(0,0,0,0)" }} >
          <View style={[styles.centerEverything, { top: 5, flex: 1, maxWidth: 40 }]}>
            <MicrosoftIcon style={{ left: -10 }} />
          </View>
          <View style={{ flex: 4, justifyContent: 'center', alignItems: 'center', }}>
            <Text adjustsFontSizeToFit style={{ color: 'black', opacity: .7, fontWeight: '600' }}>Sign in with Microsoft</Text>
          </View>
        </Animatable.View>
        </View>
        );
      }}
    </Pressable>
  );
}

export default MicrosoftButton;